import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import {
  ExtendedMoneyflowTracker,
  MoneyflowBreakdownCollection,
  MoneyflowBreakdownOwnProps,
  MoneyflowDataState,
  MoneyflowTrackerSeverity,
  MoneyflowTrendOwnProps,
  MoneyflowType,
  MoneyflowTyped,
  Transaction,
  FullMoneyflowTyped,
  MoneyflowBreakdownItem,
  TransactionUid,
} from 'interfaces/moneyflow'
import { SecondaryLine } from 'interfaces/chart'
import { selectedPeergroupIdSelector, yourPeergroupIdSelector } from 'selectors/peergroupSelectors'
import { isCollectionEmpty, removeByKeyFromCollection, getItemsByKey, getItemFromCollection } from 'helpers/normalizeHelpers'
import { isNotNullSelector, metaSelector } from 'selectors/globalSelectors'
import { ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID } from 'constants/meta'
import { MONEYFLOW_LIMIT_TYPE } from 'constants/moneyflow'
import { Trend } from 'interfaces/trend'
import { find, isNumber } from 'lodash'

const moneyflowCategorySelector = (_state: State, { categoryId }: MoneyflowTrendOwnProps) =>
  categoryId

// ===========================================================================//
//                                 TRANSACTIONS                               //
// ===========================================================================//

export const transactionsByTypeSelector = (state: State, { type }: FullMoneyflowTyped) =>
  state.app[type].transactions

export const transactionEditingIdSelector = createSelector(
  transactionsByTypeSelector,
  (transactions) => transactions.editingId
)

export const transactionsDataByTypeSelector = (state: State, { type }: FullMoneyflowTyped): MoneyflowDataState['transactions'] =>
  state.data[type].transactions

export const spendingTransactionsSelector = createSelector(
  (state: State) => state.data.transactions.transactions,
  (transactions) => {
    if (!transactions) {
      return null
    }

    return transactions.results
  }
)

export const editingTransactionSelector = createSelector(
  transactionEditingIdSelector,
  transactionsDataByTypeSelector,
  (editingId, transactions) => {
    if (!editingId || !transactions) {
      return null
    }

    const transaction = transactions.results.byKey[editingId]
    return transaction ? transaction : null
  }
)

export const recentTransactionsSelector = createSelector(
  (state: State) => state.data.recentTransactions.all,
  (state: State) => state.data.recentTransactions.recent.payload,
  (all, recentTransactions) => {
    if (!recentTransactions || !recentTransactions.length) {
      return null
    }
    return getItemsByKey<Transaction>(all.byKey, recentTransactions)
  }
)

const ownTransactionIdSelector = (_state: State, { uid }: { uid: TransactionUid }) => uid

export const recentTransactionSelector = createSelector(
  recentTransactionsSelector,
  ownTransactionIdSelector,
  (transactions, uid) => !!transactions ? find(transactions, ['uid', uid]) : null
)

export const recentCardTransactionsSelector = (state: State) => state.data.creditCard.transactions

export const recentCardTransactionSelector = createSelector(
  recentCardTransactionsSelector,
  ownTransactionIdSelector,
  getItemFromCollection
)

// ===========================================================================//
//                                 BREAKDOWN                                  //
// ===========================================================================//

export const memberMoneyflowBreakdownSelector = (state: State, { type, timeframe }: MoneyflowBreakdownOwnProps) =>
  state.data[type].breakdown[timeframe]

export const isMemberMoneyflowBreakdownLoadedSelector = createSelector(
  memberMoneyflowBreakdownSelector,
  (breakdown) => Boolean(breakdown) && !isCollectionEmpty(breakdown)
) as (state: State, { type }: MoneyflowBreakdownOwnProps) => boolean

export const peergroupsMoneyflowBreakdownsSelector = (state: State, { type, timeframe }: MoneyflowBreakdownOwnProps) =>
  state.data.peergroups[type].breakdown[timeframe]

export const isPeergroupMoneyflowBreakdownLoadedSelector = createSelector(
  peergroupsMoneyflowBreakdownsSelector,
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (breakdown, yourPeersId, selectedId) =>
    Boolean(breakdown) && [yourPeersId, selectedId].every((id) => isNumber(id) && Boolean(breakdown.byKey[id]))
) as (state: State, { type }: MoneyflowBreakdownOwnProps) => boolean


// ===========================================================================//
//                                  TRENDS                                    //
// ===========================================================================//

export const memberMoneyflowTrendsSelector = (state: State, { type, timeframe, categoryId }: MoneyflowTrendOwnProps) =>
  state.data[type].trends[timeframe][categoryId]

export const memberMoneyflowComparisonTrendsSelector = (state: State, { type, categoryId, timeframe }: MoneyflowTrendOwnProps) => {
  const compareType = type === 'spending' ? 'income' : 'spending'
  // TODO - prevent API call from being made if no comparison line is needed
  const isCategorySelected = categoryId !== ALL_CATEGORY_ID

  if (isCategorySelected) {
    return null
  }

  return state.data[compareType].trends[timeframe][ALL_CATEGORY_ID]
}

export const hasMemberMoneyflowTrendsSelector = createSelector(
  memberMoneyflowTrendsSelector,
  Boolean
) as (state: State, ownProps: MoneyflowTyped) => boolean

export const memberOppositeTotalMoneyflowTrendSelector = (state: State, { type, timeframe }: MoneyflowTrendOwnProps) => {
  const oppositeType = type === 'spending' ? 'income' : 'spending'
  const trend = state.data[oppositeType].trends[timeframe][ALL_CATEGORY_ID]

  return trend
}

export const isMemberOppositeTotalMoneyflowTrendLoadedSelector = createSelector(
  moneyflowCategorySelector,
  memberOppositeTotalMoneyflowTrendSelector,
  (categoryId, trend) =>
    categoryId !== ALL_CATEGORY_ID || Boolean(trend)
) as (state: State, ownProps: MoneyflowTrendOwnProps) => boolean

const peergroupMoneyflowTrendsSelector = (state: State, { type, timeframe, categoryId }: MoneyflowTrendOwnProps) =>
  state.data.peergroups[type].trends[timeframe][categoryId]

export const yourPeersMoneyflowTrendsSelector = createSelector(
  peergroupMoneyflowTrendsSelector,
  yourPeergroupIdSelector,
  (trends, peersId) =>
    isNumber(peersId) && trends.byKey[peersId]
      ? trends.byKey[peersId]
      : null
) as (state: State, ownProps: MoneyflowTrendOwnProps) => Trend | null

export const othersMoneyflowTrendsSelector = createSelector(
  peergroupMoneyflowTrendsSelector,
  selectedPeergroupIdSelector,
  (trends, otherId) =>
    isNumber(otherId) && trends.byKey[otherId]
      ? trends.byKey[otherId]
      : null
) as (state: State, ownProps: MoneyflowTrendOwnProps) => Trend | null

export const isPeergroupMoneyflowTrendsLoadedSelector = createSelector(
  peergroupMoneyflowTrendsSelector,
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (trends, yourPeersId, selectedId) =>
    Boolean(trends) && [yourPeersId, selectedId].every((id) => isNumber(id) && Boolean(trends.byKey[id]))
) as (state: State, ownProps: MoneyflowTrendOwnProps) => boolean


// ===========================================================================//
//                                 TRACKERS                                   //
// ===========================================================================//
export const moneyflowTrackersSelector =
  ({ data }: State, { type }: MoneyflowTyped) => data[type].trackers

export const moneyflowTrackerSelector = (state: State, { type, categoryId }: MoneyflowTrendOwnProps) => {
  const { trackers } = state.data[type]

  return trackers
    ? trackers.byKey[categoryId]
    : null
}

export const hasMoneyflowTrackersSelector = createSelector(
  moneyflowTrackersSelector,
  isNotNullSelector
) as (state: State, ownProps: MoneyflowTyped) => boolean

export const moneyflowAppTrackersSelector =
  ({ app }: State, { type }: MoneyflowTyped) => app[type].trackers

export const moneyflowTrackerLimitSelector = createSelector(
  moneyflowTrackerSelector,
  (_state: State, { type }: MoneyflowTyped) => type,
  (tracker, type): SecondaryLine | null =>
    !tracker ? null : {
      title: MONEYFLOW_LIMIT_TYPE[type],
      amount: tracker.limit,
    },
) as (state: State, ownProps: MoneyflowTrendOwnProps) => SecondaryLine | null


type CalculateSeverityParam = {
  current: number
  projection: number
  limit: number
  type: MoneyflowType
}

function calculateTrackerSeverity (tracker: CalculateSeverityParam): MoneyflowTrackerSeverity {
  const { projection, limit, current, type } = tracker

  let severity: MoneyflowTrackerSeverity = 'warning'
  if (type === 'spending') {
    if (projection <= limit) {
      severity = 'on-track'
    } else if (current > limit) {
      severity = 'danger'
    }
  }
  if (type === 'income') {
    if (projection >= limit) {
      severity = 'on-track'
    }
  }

  return severity
}

export const moneyflowExtendedTrackersSelector =
  createSelector(
    moneyflowTrackersSelector,
    memberMoneyflowBreakdownSelector,
    (_state: State, { type }: MoneyflowTyped) => type,
    (trackers, memberBreakdown, type) => {
      if (!trackers) {
        return null
      }

      const extendedTrackers: ExtendedMoneyflowTracker[] = trackers.allKeys.map((category) => {
        const { limit, id } = trackers.byKey[category]
        const { amount, projected } = memberBreakdown.byKey[category]

        const calculateParam: CalculateSeverityParam = {
          limit,
          projection: projected || 0,
          current: amount,
          type,
        }

        return {
          ...calculateParam,
          id,
          category,
          severity: calculateTrackerSeverity(calculateParam),
        }
      })

      return extendedTrackers
    }
  )


export const metaMoneyflowCategoriesSelector = (state: State, { type }: MoneyflowTyped) => {
  const meta = metaSelector(state)

  return type === 'spending'
    ? meta.spending_categories
    : meta.income_categories
}

// ===========================================================================//
//                                PIE CHART                                   //
// ===========================================================================//
type CategoriesBreakdowns = [MoneyflowBreakdownCollection, MoneyflowBreakdownCollection, MoneyflowBreakdownCollection]
export const categoriesMoneyflowBreakdownSelector = createSelector(
  memberMoneyflowBreakdownSelector,
  peergroupsMoneyflowBreakdownsSelector,
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  (memberBreakdown, peergroupBreakdowns, peersId, othersId): CategoriesBreakdowns | null =>
    isNumber(peersId) && isNumber(othersId)
      && peergroupBreakdowns.byKey[peersId] && peergroupBreakdowns.byKey[othersId]
      ? ([
        memberBreakdown,
        peergroupBreakdowns.byKey[peersId!].breakdown,
        peergroupBreakdowns.byKey[othersId!].breakdown,
      ])
        .map((breakdown) =>
          removeByKeyFromCollection<MoneyflowBreakdownItem, 'category'>(breakdown, ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID)) as CategoriesBreakdowns
      : null,
)

export const categoriesMoneyflowAppSelector = (state: State, { type }: MoneyflowTyped) =>
  state.app[type].categories
